/**
 * Rate guard for the login-route bootstrap probe (`appInitializerFactory`).
 *
 * That probe posts `/auth/refresh` on every load of the login page, directly through `AuthService`
 * — outside `SessionCoordinatorService`, and so outside the spacing every other refresh trigger
 * obeys. Once per page load is harmless; it is a refresh storm the moment the page reloads in a
 * loop (a stale lazy chunk after a deploy, a redirect ping-pong, a tab reopened over and over).
 * `POST /auth/refresh` is capped at 5/min per session — and per *user*, so this app and the
 * backoffice share one budget — and the 429 it answers with reads to the app as a session lost.
 *
 * The window is shared across tabs — localStorage, not sessionStorage — because several login tabs
 * are still one session to the API's rate limit. Skipping a probe costs only the automatic hop to
 * the dashboard: the login form is on screen either way.
 */
const PROBE_KEY = 'auth_bootstrap_probe_at';
/**
 * 30 s, so a page reloading on itself costs at most 2 probes/min — under the endpoint's 5/min cap
 * with room to spare. A 10 s window would allow 6/min and trip it on its own.
 */
const PROBE_WINDOW_MS = 30_000;

/** True when the bootstrap probe may run; records the attempt as a side effect. */
export function shouldProbeSession(now: number = Date.now()): boolean {
  try {
    const last = Number(localStorage.getItem(PROBE_KEY));
    const elapsed = now - last;
    // `elapsed >= 0` keeps a clock that moved backwards from blocking the probe indefinitely.
    if (last > 0 && elapsed >= 0 && elapsed < PROBE_WINDOW_MS) {
      return false;
    }

    localStorage.setItem(PROBE_KEY, String(now));
    return true;
  } catch {
    // Storage unavailable (private mode, hardened settings): probing beats never restoring.
    return true;
  }
}
